import { roundCurrency } from '../../../components/promotionsV2/helper/utils';

import { PROMO_BRAND_PATTERN, PROMO_NAME_PATTERN, PROMO_SUPPORTED_BEAUTIFIED_KEYS } from './constants';
import { isAsianRegion } from './helper';
import { CampaignType } from './types/campaign';
import * as T from './types/promotions';
import * as A from './types/promotionsApi';
import { DynamicField } from './types/promotionsApi';

type PromoParams = {
  casinoRegion: string;
  currency: string;
  lang: string;
};

type MapToEnum<S, E> = S extends string ? E : S;

export const parsePromotionJSON = (json: A.PromotionJSON, params: PromoParams): T.Promotion<T.PromotionType> => {
  switch (json.promoType) {
    case T.PromotionType.Tournament: {
      return parseTournament(json, params);
    }
    case T.PromotionType.Prizedrop: {
      return parsePrizeDrop(json, params);
    }
    case T.PromotionType.Cashback: {
      return parseCashback(json, params);
    }
    default: {
      throw new Error('Promo type not configured');
    }
  }
};

export const parsePromoCommonFields = (
  json: A.PromotionJSON
): Pick<
  T.Promotion<T.PromotionType>,
  | 'name'
  | 'brandName'
  | 'opted'
  | 'consentRequired'
  | 'activeDuration'
  | 'gameIds'
  | 'campaignDetails'
  | 'campaignType'
> => {
  return {
    name: parseName(json.name),
    brandName: getBrandName(json.name),
    opted: json.opt as MapToEnum<typeof json.opt, T.PromotionOptedStatus>,
    consentRequired: json.consent,
    activeDuration: { fromEpoch: json.startDate, toEpoch: json.endDate },
    gameIds: json.gameIds.split(','),
    campaignType: CampaignType.DefaultPromo,
    campaignDetails: {
      id: json.campaignId,
      endDate: json.campaignEndDate,
    },
  };
};

export const parseTournament = (
  json: A.TournamentPromotionJSON,
  params: PromoParams
): T.Promotion<T.PromotionType.Tournament> => {
  const defaultDynamicFields: T.DefaultDynamicFields = {
    tournamentName: json.name ?? '',
    minBet: json.TOURNAMENT.minBetLimit ?? '',
    totalPrizeAmount: json.PRIZE_DETAILS.prizeAmount,
    totalPrizeAmountBeautified: roundCurrency(json.PRIZE_DETAILS.prizeAmount),
    highestPrize: json.PRIZE_DETAILS.highestPrize,
    highestPrizeBeautified: roundCurrency(Number(json.PRIZE_DETAILS.highestPrize)),
    ddwName: isAsianRegion(params.casinoRegion, json.TOURNAMENT.envId)
      ? T.DDWBrandName.DailyWins
      : T.DDWBrandName.DropsAndWins,
  };
  const updatedDynamicFieldsMap = updatedDynamicFieldMap(json.dynamicFields);
  const { howToPlayFirstRule, howToPlaySecondRule, howToPlayThirdRule } = json.TOURNAMENT_DETAILS;
  return {
    ...parsePromoCommonFields(json),
    type: T.PromotionType.Tournament,
    id: json.tournamentId,
    platformId: json.platformTournamentId,
    roundsType: json.TOURNAMENT_DETAILS.roundsType as MapToEnum<
      typeof json.TOURNAMENT_DETAILS.roundsType,
      T.PromoRoundType
    >,
    ruleDetails: {
      howToPlay: [howToPlayFirstRule, howToPlaySecondRule, howToPlayThirdRule].filter((val) => !!val),
      rule: json.TOURNAMENT_DETAILS.rule,
      ruleShort: json.TOURNAMENT_DETAILS.shortRule,
      dynamic: { ...defaultDynamicFields, ...updatedDynamicFieldsMap },
      currency: params.currency,
      lang: params.lang,
    },
    isAsian: isAsianRegion(params.casinoRegion, json.TOURNAMENT.envId),
    prizeDetails: parseTournamentPrizeDetails(json),
    userPrizeDetails: {
      leaderboard: parseLeaderboardDetails(json.LEADERBOARD),
      leaderboard_aroundMe: parseLeaderboardAroundMeDetails(json.LEADERBOARD_AROUNDME),
    },
  };
};

export const parsePrizeDrop = (
  json: A.PrizeDropPromotionJSON,
  params: PromoParams
): T.Promotion<T.PromotionType.Prizedrop> => {
  const defaultDynamicFields: T.DefaultDynamicFields = {
    tournamentName: json.name ?? '',
    minBet: json.PRIZEDROP.minBetLimit ?? '',
    maxBet: json.maxBetLimitForBetMultiplier ?? '',
    totalPrizeAmount: json.PRIZE_DETAILS.prizeAmount,
    totalPrizeAmountBeautified: roundCurrency(json.PRIZE_DETAILS.prizeAmount),
    highestPrize: json.PRIZE_DETAILS.highestPrize,
    highestPrizeBeautified: roundCurrency(Number(json.PRIZE_DETAILS.highestPrize)),
    ddwName: isAsianRegion(params.casinoRegion, json.PRIZEDROP.envId)
      ? T.DDWBrandName.DailyWins
      : T.DDWBrandName.DropsAndWins,
  };

  const updatedDynamicFieldsMap = updatedDynamicFieldMap(json.dynamicFields);
  const { howToPlayFirstRule, howToPlaySecondRule, howToPlayThirdRule } = json.PRIZEDROP_DETAILS;

  return {
    ...parsePromoCommonFields(json),
    type: T.PromotionType.Prizedrop,
    id: json.prizedropId,
    platformId: json.platformPrizedropId,
    roundsType: json.PRIZEDROP.roundsType as MapToEnum<typeof json.PRIZEDROP.roundsType, T.PromoRoundType>,
    ruleDetails: {
      howToPlay: [howToPlayFirstRule, howToPlaySecondRule, howToPlayThirdRule].filter((val) => !!val),
      rule: json.PRIZEDROP_DETAILS.rule,
      ruleShort: json.PRIZEDROP_DETAILS.shortRule,
      dynamic: { ...defaultDynamicFields, ...updatedDynamicFieldsMap },
      currency: params.currency,
      lang: params.lang,
    },
    isAsian: isAsianRegion(params.casinoRegion, json.PRIZEDROP.envId),
    prizeDetails: parsePrizeDropPrizeDetails(json),
    userPrizeDetails: { winners: [], screenNameHidden: false },
  };
};

export const parseCashback = (
  json: A.CashbackPromotionJSON,
  params: PromoParams
): T.Promotion<T.PromotionType.Cashback> => {
  const defaultDynamicFields: T.DefaultDynamicFields = {
    tournamentName: json.name ?? '',
    minBet: json.CASHBACK.minBetLimit ?? '',
  };
  const { howToPlayFirstRule, howToPlaySecondRule, howToPlayThirdRule } = json.CASHBACK_DETAILS;

  return {
    ...parsePromoCommonFields(json),
    type: T.PromotionType.Cashback,
    id: json.cashbackId,
    platformId: json.platformCashbackId,
    roundsType: json.CASHBACK_DETAILS.roundsType as MapToEnum<
      typeof json.CASHBACK_DETAILS.roundsType,
      T.PromoRoundType
    >,
    ruleDetails: {
      howToPlay: [howToPlayFirstRule, howToPlaySecondRule, howToPlayThirdRule].filter((val) => !!val),
      rule: json.CASHBACK_DETAILS.rule,
      ruleShort: json.CASHBACK_DETAILS.shortRule,
      dynamic: { ...defaultDynamicFields, ...json.dynamicFields },
      currency: params.currency,
      lang: params.lang,
    },
    isAsian: isAsianRegion(params.casinoRegion, json.CASHBACK.envId),
    prizeDetails: parseCashbackPrizeDetails(json),
    userPrizeDetails: parseCashbackUserScoreDetails(json),
  };
};

export const parseLeaderboardDetails = (json: A.LeaderboardAPIResponse): T.TournamentLeaderboardDetails => {
  const { data: leaderboardDetails } = json;
  return {
    data: leaderboardDetails.map((leaderboard) => ({
      playerId: leaderboard.platformTournamentPlayerId,
      screenName: leaderboard.screenName ?? '',
      position: leaderboard.position,
      score: leaderboard.score,
      scoreDate: leaderboard.scoreDate,
    })),
    screenNameHidden: json.screenNameHidden,
  };
};

export const parseLeaderboardAroundMeDetails = (
  json: A.LeaderboardAroundMeAPIResponse
): T.TournamentLeaderboardAroundMeDetails => {
  const { leaderboardItems } = json;
  return {
    playerId: json.platformTournamentPlayerId,
    leaderboardItems: (leaderboardItems ?? []).map((data) => ({
      playerId: data.platformTournamentPlayerId,
      screenName: data.screenName ?? '',
      scoreDate: data.scoreDate,
      position: data.position,
      score: data.score,
    })),
  };
};

export const parsePrizeDropPrize = (json: A.PrizesAndWinnersAPIResponse, id: number): T.PrizedropPrizeType[] => {
  const { prizes } = json[id] ?? {};
  if (prizes) {
    return prizes.map((prize) => ({
      id: prize.prizeId,
      size: prize.currentSize,
      originalSize: prize.originalSize,
      prizeDetails: parsePromotionPrizeJSON(prize),
    }));
  }
  return [];
};

export const parsePrizeDropWinners = (json: A.PrizesAndWinnersAPIResponse, id: number): T.PrizeDropWinnersType[] => {
  const { winners: winnersDetails } = json[id] ?? {};
  if (winnersDetails) {
    return winnersDetails.map((win) => ({
      amount: win.amount,
      casinoUserId: win.casinoUserId,
      gift: win.gift,
      prizeId: win.prizeId,
      screenName: win.screenName,
      winDate: win.winDate,
    }));
  }
  return [];
};

export const parseCashbackScore = (
  json: A.CashbackScoreAPIResponse,
  cashbackId: number
): T.CashbackScoreType | undefined => {
  const score = json[`${cashbackId}`];
  if (score) {
    return {
      totalScore: score.totalScore,
      cashbackScore: score.cashbackScore,
      cashbackPercentage: score.cashbackPercent,
      roundLimitExceeded: score.isRoundLimitExceeded,
    };
  }
};

export const parsePromotionWinNotificationJSON = (
  json: A.PromotionWinNotificationJSON,
  params: PromoParams
): T.WinNotificationType => {
  const defaultDynamicFields: T.DefaultDynamicFields = {
    tournamentName: json.name ?? '',
    minBet: json.minBetLimit ?? '',
    totalPrizeAmount: json.extraData.PRIZE_DETAILS?.prizeAmount ?? '',
    totalPrizeAmountBeautified:
      json.extraData?.PRIZE_DETAILS?.prizeAmount && roundCurrency(json.extraData.PRIZE_DETAILS.prizeAmount),
    highestPrize: json.extraData.PRIZE_DETAILS?.highestPrize ?? '',
    highestPrizeBeautified:
      json.extraData.PRIZE_DETAILS?.highestPrize && roundCurrency(Number(json.extraData.PRIZE_DETAILS.highestPrize)),
    ddwName: isAsianRegion(params.casinoRegion, json.envId) ? T.DDWBrandName.DailyWins : T.DDWBrandName.DropsAndWins,
  };

  return {
    promoType: json.promoType as MapToEnum<typeof json.promoType, T.PromotionType>,
    promoId: json.promoId,
    name: parseName(json.name),
    brandName: getBrandName(json.name),
    isAsia: isAsianRegion(params.casinoRegion, json.envId),
    winType: json.winType as MapToEnum<typeof json.winType, T.PromotionPrizeType>,
    prize: json.prize,
    endDate: Number(json.endDate),
    ruleDetails: {
      rule: json.rule,
      dynamic: { ...defaultDynamicFields, ...json.dynamicFields },
      currency: params.currency,
      lang: params.lang,
    },
    autoPay: json.autoPay,
    minBetLimit: json.minBetLimit,
    extraData: {
      type: json.extraData.type as MapToEnum<typeof json.extraData.type, T.CashbackPromoType>,
      minCashback: json.extraData.minCashbackAmount,
      maxCashback: json.extraData.maxCashbackAmount,
      totalNoOfPrizes: json.extraData.PRIZE_DETAILS?.prizeCount,
      totalPrizeAmount: json.extraData.PRIZE_DETAILS?.prizeAmount,
      highestPrize: json.extraData.PRIZE_DETAILS?.highestPrize,
      prizes: json.extraData.PRIZE_DETAILS?.tournamentPrizes?.map((prize) => ({
        fromRank: prize.from,
        toRank: prize.to,
        prizeDetails: parsePromotionPrizeJSON(prize),
      })),
    },
  };
};

const parsePrizeDropPrizeDetails = (json: A.PrizeDropPromotionJSON): T.PrizedropPrizeDetails => {
  const { PRIZEDROP: prizeDrop } = json;
  return {
    minimumBetLimit: prizeDrop.minBetLimit ? prizeDrop.minBetLimit : 0,
    totalPrizeAmout: json.PRIZE_DETAILS.prizeAmount,
    totalNoOfPrizes: json.PRIZE_DETAILS.prizeCount,
    highestPrize: json.PRIZE_DETAILS.highestPrize,
    prizes: prizeDrop.prizes.map((prize) => ({
      id: prize.prizeId,
      size: prize.currentSize,
      originalSize: prize.originalSize,
      prizeDetails: parsePromotionPrizeJSON(prize),
    })),
  };
};

const parseCashbackUserScoreDetails = (json: A.CashbackPromotionJSON): T.CashbackUserScoreDetails => {
  const { score, CASHBACK_DETAILS: cashbackDetails } = json;
  return {
    totalScore: score.totalScore,
    cashbackScore: score.cashbackScore,
    cashbackPercentage: score.cashbackPercent,
    roundLimitExceeded: score.isRoundLimitExceeded,
    displayScore: cashbackDetails.cashbackValues.displayScore,
  };
};

//
// Helper functions
//

const updatedDynamicFieldMap = (
  dynamicFieldMap: Record<string, string | DynamicField> | undefined
): Record<string, string | DynamicField> => {
  const updatedMap = { ...dynamicFieldMap };
  PROMO_SUPPORTED_BEAUTIFIED_KEYS.forEach((field) => {
    if (updatedMap[field] && typeof updatedMap[field] !== 'object') {
      updatedMap[field] = String(roundCurrency(Number(updatedMap[field])));
    }
  });

  return updatedMap;
};

const parseName = (name: string): T.Promotion['name'] => {
  return name.replace(PROMO_NAME_PATTERN, '');
};

const getBrandName = (name: string): string => {
  const match = PROMO_BRAND_PATTERN.exec(name);
  const brandName = match?.[1];
  return brandName ?? '';
};

const parseTournamentPrizeDetails = (json: A.TournamentPromotionJSON): T.TournamentPrizeDetails => {
  const { TOURNAMENT: tournament, TOURNAMENT_DETAILS: tournamentDetails } = json;
  return {
    minimumBetLimit: tournament.minBetLimit ? tournament.minBetLimit : 0,
    prizes: tournamentDetails.prizes.map((prize) => ({
      fromRank: prize.from,
      toRank: prize.to,
      prizeDetails: parsePromotionPrizeJSON(prize),
    })),
    winCriterion: parseTournamentPromotionWinCriterion(tournamentDetails),
    totalNoOfPrizes: json.PRIZE_DETAILS.prizeCount,
    totalPrizeAmout: json.PRIZE_DETAILS.prizeAmount,
    highestPrize: json.PRIZE_DETAILS.highestPrize,
  };
};

const parseCashbackPrizeDetails = (json: A.CashbackPromotionJSON): T.CashbackPrizeDetails => {
  const {
    CASHBACK_DETAILS: cashbackDetails,
    CASHBACK: { minBetLimit },
  } = json;
  return {
    minimumBetLimit: minBetLimit ?? 0,
    minCashbackAmount: cashbackDetails.cashbackValues.minCashbackAmount,
    maxCashbackAmount: cashbackDetails.cashbackValues.maxCashbackAmount,
    type: cashbackDetails.cashbackValues.type as T.CashbackPromoType,
  };
};

const parsePromotionPrizeJSON = (prize: A.PromotionPrizeJSON): T.PromotionPrize => {
  const invalid = T.PromotionPrizeType.Invalid;
  const prizeType = prize.type as MapToEnum<typeof prize.type, T.PromotionPrizeType>;
  switch (prizeType) {
    case T.PromotionPrizeType.Gift: {
      const gift = prize.gift ?? null;
      return gift !== null ? { type: prizeType, gift } : { type: invalid };
    }
    case T.PromotionPrizeType.Coins: {
      const amount = prize.amount ?? null;
      return amount !== null ? { type: prizeType, amount } : { type: invalid };
    }
    case T.PromotionPrizeType.BetMultiplier: {
      const multiplier = prize.betMultiplier ?? null;
      return multiplier !== null ? { type: prizeType, multiplier } : { type: invalid };
    }
    default:
      return { type: invalid };
  }
};

const parseTournamentPromotionWinCriterion = (
  json: A.TournamentPromotionJSON['TOURNAMENT_DETAILS']
): T.TournamentPromoWinCriterionType => {
  const winCriterionType = json.winCriterion as MapToEnum<typeof json.winCriterion, T.TournamentPromoWinCriterionType>;
  return winCriterionType;
};
